import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { payments, isNativeAndroid } from "@/lib/payments";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, Loader2, Store, BadgeCheck, Check, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import OwnershipDisclaimer from "@/components/OwnershipDisclaimer";
import { ProductCover } from "@/components/ProductCover";
import { WhatsInside } from "@/components/WhatsInside";
import { FollowButton } from "@/components/FollowButton";

const FALLBACK_AVATAR = "https://images.pexels.com/photos/6446709/pexels-photo-6446709.jpeg";

export default function SellerStorefront() {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [owned, setOwned] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/sellers/${sellerId}`);
        setData(r.data);
      } catch {
        toast.error("Seller not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [sellerId]);

  const buy = async (p) => {
    if (!user) {
      toast.message("Sign in to buy");
      navigate(`/login?next=/seller/${sellerId}`);
      return;
    }
    setBusyId(p.product_id);
    try {
      if (isNativeAndroid()) {
        await payments.buy(p);
        setOwned((prev) => new Set([...prev, p.product_id]));
        toast.success(`Purchased "${p.name}"`);
        setBusyId(null);
      } else {
        await payments.buy(p); // redirects to Stripe on web
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Could not start checkout");
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-[#9CA3AF] gap-2 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading storefront…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0A0A0A] text-[#F3F4F6] gap-4">
        <div className="text-lg">Seller not found</div>
        <Link to="/market"><Button variant="ghost" className="text-[#9CA3AF]">Back to Marketplace</Button></Link>
      </div>
    );
  }

  const { seller, products } = data;
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const initials = (seller.name || "?").trim().slice(0, 1).toUpperCase();

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `${seller.name} on NoteLosh`, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied");
      }
    } catch { /* user cancelled */ }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F3F4F6]" data-testid="seller-storefront">
      <header className="px-6 py-4 border-b border-[#1E1E1E] flex items-center justify-between sticky top-0 bg-[#0A0A0A]/90 backdrop-blur z-10">
        <div className="flex items-center gap-3">
          <Link to="/market">
            <Button variant="ghost" size="icon" className="text-[#9CA3AF] hover:bg-[#1E1E1E]">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="font-heading text-xl font-bold flex items-center gap-2">
            <Store className="h-5 w-5 text-[#3B82F6]" /> Seller
          </h1>
        </div>
        <Button
          onClick={share}
          data-testid="seller-share-btn"
          variant="ghost"
          className="rounded-full text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#1E1E1E] text-xs h-8"
        >
          Share storefront
        </Button>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Seller hero */}
        <section className="flex items-center gap-4 rounded-2xl bg-[#121212] border border-[#1E1E1E] p-5">
          {seller.picture ? (
            <img
              src={seller.picture}
              alt=""
              className="h-16 w-16 rounded-full object-cover border border-[#27272A]"
              onError={(e) => { e.currentTarget.src = FALLBACK_AVATAR; }}
            />
          ) : (
            <div className="h-16 w-16 rounded-full flex items-center justify-center text-xl font-semibold text-white bg-gradient-to-br from-[#2563EB] to-[#1D4ED8]">
              {initials}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 data-testid="seller-name" className="font-heading text-xl font-bold truncate">{seller.name}</h2>
              {seller.is_official && (
                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#2563EB]/15 border border-[#2563EB]/30 text-[#3B82F6]">
                  <BadgeCheck className="h-3 w-3" /> Official
                </span>
              )}
              {seller.verified && !seller.is_official && (
                <span
                  data-testid="seller-verified-badge"
                  className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#10B981]/15 border border-[#10B981]/30 text-[#10B981]"
                >
                  <BadgeCheck className="h-3 w-3" /> Verified Seller
                </span>
              )}
            </div>
            <div className="text-xs text-[#9CA3AF] mt-0.5">
              {seller.product_count} listing{seller.product_count === 1 ? "" : "s"} · {seller.total_sold} sale{seller.total_sold === 1 ? "" : "s"}
            </div>
          </div>
          <FollowButton sellerId={seller.seller_id || sellerId} signedIn={!!user} />
        </section>

        {/* Products grid */}
        <section>
          <h3 className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-3 flex items-center gap-1.5">
            <ShoppingBag className="h-3 w-3" /> Listings
          </h3>
          {products.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#27272A] p-10 text-center text-sm text-[#6B7280]">
              This seller hasn't published anything yet.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((p) => (
                <article
                  key={p.product_id}
                  data-testid={`seller-product-${p.product_id}`}
                  className="rounded-2xl bg-[#121212] border border-[#1E1E1E] overflow-hidden hover:border-[#27272A] transition-all"
                >
                  <div className="h-32 w-full bg-[#1E1E1E] overflow-hidden">
                    <ProductCover p={p} />
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="text-[10px] uppercase tracking-wider text-[#3B82F6]">
                      {p.kind === "wallpaper" ? "Wallpaper" : "Note pack"}
                    </div>
                    <h4 className="font-heading font-semibold text-sm leading-tight line-clamp-1">{p.name}</h4>
                    <p className="text-xs text-[#9CA3AF] line-clamp-2">{p.description}</p>
                    <WhatsInside p={p} />
                    <OwnershipDisclaimer product={p} />
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-sm font-semibold">${(p.price_cents / 100).toFixed(2)}</span>
                      <Button
                        onClick={() => buy(p)}
                        disabled={busyId === p.product_id || owned.has(p.product_id)}
                        data-testid={`seller-buy-${p.product_id}`}
                        className="rounded-full h-8 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs"
                      >
                        {owned.has(p.product_id) ? (
                          <><Check className="h-3 w-3 mr-1"/>Owned</>
                        ) : busyId === p.product_id ? (
                          <><Loader2 className="h-3 w-3 mr-1 animate-spin"/>Opening…</>
                        ) : (
                          "Buy"
                        )}
                      </Button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
